import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { DashboardLayout } from '../components/layouts/DashboardLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
    Play,
    Pause,
    ChevronLeft,
    ChevronRight,
    CheckCircle,
    Clock,
    Settings,
    Volume2,
    VolumeX,
    Maximize,
    Youtube
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to check if URL is YouTube (including Shorts)
const isYoutubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com/embed/') || 
           url.includes('youtube.com/watch') || 
           url.includes('youtube.com/shorts/') ||
           url.includes('youtu.be/');
};

export const LessonPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, language, getLocalizedField } = useLanguage();
    const videoRef = useRef(null);
    
    const [lesson, setLesson] = useState(null);
    const [chapter, setChapter] = useState(null);
    const [level, setLevel] = useState(null);
    const [progress, setProgress] = useState(null);
    const [allLessons, setAllLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);

    useEffect(() => {
        fetchLessonData();
    }, [id]);

    const fetchLessonData = async () => {
        setLoading(true);
        try {
            const [lessonRes, chaptersRes, levelsRes, lessonsRes, progressRes] = await Promise.all([
                axios.get(`${API}/lessons/${id}`),
                axios.get(`${API}/chapters`),
                axios.get(`${API}/levels`),
                axios.get(`${API}/lessons`),
                axios.get(`${API}/progress`)
            ]);

            const lessonData = lessonRes.data;
            setLesson(lessonData);

            const chapterData = chaptersRes.data.find(c => c.id === lessonData.chapter_id);
            setChapter(chapterData);

            if (chapterData) {
                const levelData = levelsRes.data.find(l => l.id === chapterData.level_id);
                setLevel(levelData);
            }

            // Get all lessons in the same chapter
            const chapterLessons = lessonsRes.data
                .filter(l => l.chapter_id === lessonData.chapter_id)
                .sort((a, b) => a.order - b.order);
            setAllLessons(chapterLessons);

            // Get user progress for this lesson
            const lessonProgress = progressRes.data.find(p => p.lesson_id === id);
            setProgress(lessonProgress);

            // Resume video from last position
            if (lessonProgress?.video_position && videoRef.current) {
                videoRef.current.currentTime = lessonProgress.video_position;
            }
        } catch (error) {
            console.error('Error fetching lesson:', error);
            toast.error(language === 'id' ? 'Gagal memuat lesson' : 'Failed to load lesson');
        } finally {
            setLoading(false);
        }
    };

    const updateProgress = async (completed = false, videoPosition = currentTime) => {
        try {
            await axios.post(`${API}/progress`, {
                lesson_id: id,
                completed,
                video_position: videoPosition
            });

            if (completed) {
                // Check for new badges/certificates
                const result = await axios.post(`${API}/certificates/check`);
                if (result.data.awarded?.length > 0) {
                    result.data.awarded.forEach(award => {
                        toast.success(
                            language === 'id' 
                                ? `Selamat! Anda mendapatkan ${award.type}: ${award.name}`
                                : `Congratulations! You earned ${award.type}: ${award.name}`
                        );
                    });
                }
            }
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    };

    const handleVideoTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(videoRef.current.currentTime);
            setDuration(videoRef.current.duration);
        }
    };

    const handleVideoEnded = () => {
        setIsPlaying(false);
        updateProgress(true, duration);
        toast.success(language === 'id' ? 'Lesson selesai!' : 'Lesson completed!');
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
                updateProgress(false, videoRef.current.currentTime);
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const changePlaybackRate = (rate) => {
        if (videoRef.current) {
            videoRef.current.playbackRate = rate;
            setPlaybackRate(rate);
        }
    };

    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };

    const handleSeek = (e) => {
        if (videoRef.current) {
            const rect = e.currentTarget.getBoundingClientRect();
            const percent = (e.clientX - rect.left) / rect.width;
            videoRef.current.currentTime = percent * duration;
        }
    };

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const getCurrentLessonIndex = () => allLessons.findIndex(l => l.id === id);

    const goToNextLesson = () => {
        const currentIndex = getCurrentLessonIndex();
        if (currentIndex < allLessons.length - 1) {
            navigate(`/lesson/${allLessons[currentIndex + 1].id}`);
        }
    };

    const goToPreviousLesson = () => {
        const currentIndex = getCurrentLessonIndex();
        if (currentIndex > 0) {
            navigate(`/lesson/${allLessons[currentIndex - 1].id}`);
        }
    };

    const markAsComplete = async () => {
        await updateProgress(true, duration);
        toast.success(language === 'id' ? 'Lesson ditandai selesai!' : 'Lesson marked as complete!');
        fetchLessonData();
    };

    if (loading) {
        return (
            <DashboardLayout>
                <div className="space-y-6">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="aspect-video w-full" />
                    <Skeleton className="h-24 w-full" />
                </div>
            </DashboardLayout>
        );
    }

    if (!lesson) {
        return (
            <DashboardLayout>
                <div className="text-center py-12">
                    <p className="text-slate-500">Lesson not found</p>
                    <Link to="/learning">
                        <Button className="mt-4">{t('back')}</Button>
                    </Link>
                </div>
            </DashboardLayout>
        );
    }

    const currentIndex = getCurrentLessonIndex();
    const hasPrevious = currentIndex > 0;
    const hasNext = currentIndex < allLessons.length - 1;

    return (
        <DashboardLayout>
            <div className="max-w-5xl mx-auto space-y-6 animate-fade-in">
                {/* Back Navigation */}
                <div className="flex items-center gap-2 text-sm">
                    <Link 
                        to={level ? `/learning/${level.name.toLowerCase()}` : '/learning'}
                        className="text-slate-500 hover:text-slate-700"
                    >
                        {getLocalizedField(level, 'name') || t('learningPath')}
                    </Link>
                    <ChevronRight className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-700">{getLocalizedField(chapter, 'title')}</span>
                </div>

                {/* Video Player */}
                <Card className="overflow-hidden bg-slate-900" data-testid="video-player">
                    <div className="aspect-video relative">
                        {lesson.video_url ? (
                            isYoutubeUrl(lesson.video_url) ? (
                                /* YouTube Embed Player */
                                <iframe
                                    src={lesson.video_url}
                                    className="w-full h-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                    allowFullScreen
                                    title={getLocalizedField(lesson, 'title')}
                                />
                            ) : (
                                /* Native Video Player */
                                <video
                                    ref={videoRef}
                                    src={lesson.video_url}
                                    className="w-full h-full object-contain"
                                    onTimeUpdate={handleVideoTimeUpdate}
                                    onEnded={handleVideoEnded}
                                    onLoadedMetadata={handleVideoTimeUpdate}
                                />
                            )
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-slate-800">
                                <div className="text-center">
                                    <Play className="w-16 h-16 text-slate-600 mx-auto mb-4" />
                                    <p className="text-slate-400">
                                        {language === 'id' ? 'Video belum tersedia' : 'Video not available yet'}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Video Controls - Only show for non-YouTube videos */}
                    {lesson.video_url && !isYoutubeUrl(lesson.video_url) && (
                    <div className="p-4 bg-slate-800/90">
                        {/* Progress Bar */}
                        <div 
                            className="h-1 bg-slate-600 rounded-full cursor-pointer mb-4"
                            onClick={handleSeek}
                        >
                            <div 
                                className="h-full bg-primary rounded-full transition-all"
                                style={{ width: `${duration ? (currentTime / duration) * 100 : 0}%` }}
                            />
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-white hover:bg-slate-700"
                                    onClick={togglePlay}
                                    data-testid="play-btn"
                                >
                                    {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
                                </Button>

                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="text-white hover:bg-slate-700"
                                    onClick={toggleMute}
                                >
                                    {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                </Button>

                                <span className="text-white text-sm font-mono">
                                    {formatTime(currentTime)} / {formatTime(duration || lesson.duration_minutes * 60)}
                                </span>
                            </div>

                            <div className="flex items-center gap-2">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button variant="ghost" size="sm" className="text-white hover:bg-slate-700" data-testid="speed-btn">
                                            <Settings className="w-4 h-4 mr-2" />
                                            {playbackRate}x
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent>
                                        {[0.5, 0.75, 1, 1.25, 1.5, 2].map((rate) => (
                                            <DropdownMenuItem
                                                key={rate}
                                                onClick={() => changePlaybackRate(rate)}
                                                className={playbackRate === rate ? 'bg-primary/10' : ''}
                                            >
                                                {rate}x
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                        </div>
                    </div>
                    )}
                    
                    {/* YouTube Video Info */}
                    {lesson.video_url && isYoutubeUrl(lesson.video_url) && (
                        <div className="p-4 bg-slate-800/90 flex items-center gap-3">
                            <Youtube className="w-6 h-6 text-red-500" />
                            <span className="text-white text-sm">
                                {language === 'id' 
                                    ? 'Video ini dimainkan dari YouTube. Gunakan kawalan YouTube player.'
                                    : 'This video is played from YouTube. Use YouTube player controls.'
                                }
                            </span>
                        </div>
                    )}
                </Card>

                {/* Lesson Info */}
                <Card>
                    <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                            <div>
                                <h1 className="text-2xl font-heading font-bold text-slate-900">
                                    {getLocalizedField(lesson, 'title')}
                                </h1>
                                {lesson.description && (
                                    <p className="text-slate-500 mt-2">
                                        {getLocalizedField(lesson, 'description')}
                                    </p>
                                )}
                                <div className="flex items-center gap-4 mt-4 text-sm text-slate-500">
                                    <span className="flex items-center gap-1">
                                        <Clock className="w-4 h-4" />
                                        {lesson.duration_minutes} {t('minutes')}
                                    </span>
                                    {progress?.completed && (
                                        <span className="flex items-center gap-1 text-emerald-600">
                                            <CheckCircle className="w-4 h-4" />
                                            {t('completed')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {!progress?.completed && (
                                <Button onClick={markAsComplete} data-testid="complete-btn">
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    {t('markAsComplete')}
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Navigation */}
                <div className="flex items-center justify-between">
                    <Button
                        variant="outline"
                        onClick={goToPreviousLesson}
                        disabled={!hasPrevious}
                        data-testid="prev-lesson-btn"
                    >
                        <ChevronLeft className="w-4 h-4 mr-2" />
                        {t('previousLesson')}
                    </Button>

                    <span className="text-sm text-slate-500">
                        {currentIndex + 1} / {allLessons.length}
                    </span>

                    <Button
                        onClick={goToNextLesson}
                        disabled={!hasNext}
                        data-testid="next-lesson-btn"
                    >
                        {t('nextLesson')}
                        <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        </DashboardLayout>
    );
};
